import { ensureAnonSession, supabase } from '../lib/supabase'
import { projectAccess, resolveDeliverableId } from './sharedReview'

/**
 * Transcript comments.
 *
 * Notes a reader leaves against the transcript — "love this bit", "can we
 * tighten this sentence" — stored in Supabase so everyone on the project link
 * and everyone in the studio sees the same ones.
 *
 * Deliberately NOT change_requests. A change request drives the review
 * workflow: it puts a deliverable into Changes Requested, appears in Needs
 * Attention, and waits to be marked completed. A transcript note is often
 * simply an observation, so forcing it through that state would misreport the
 * project. This is its own small table, read and written through exactly the
 * same access model (projectAccess → has_project_access), and append-only like
 * every other stored review record.
 *
 * Anchoring: the quoted passage is stored with the comment, alongside the
 * revision it was made against and where in that revision it sat. A later edit
 * therefore never drops a comment — at worst the passage can no longer be
 * found, and the comment still carries the words it was about.
 *
 * Since 28 Sep 2026 a comment also carries WHEN the passage was said, taken
 * from the transcript's cues at the moment it was written. Stored as seconds
 * rather than as "0:42": one place formats a position in a film, and a number
 * survives a film turning out to be long enough to need hours.
 */

export interface TranscriptComment {
  id: string
  authorName: string
  body: string
  /** The passage this refers to. Empty for a comment about the whole transcript. */
  quote: string
  /** Character offset of the quote in the revision it was made against. */
  quoteStart: number
  /**
   * Seconds into the media where the passage begins, or null. Null is
   * ordinary, not broken: a general comment has no passage, a typed or
   * uploaded transcript has no timings, and nothing written before 28 Sep
   * 2026 has one.
   */
  quoteSeconds: number | null
  at: number
}

export type CommentLoad =
  | { status: 'ok'; comments: TranscriptComment[] }
  | { status: 'error'; message: string }

/** The comments table has not been created yet. */
const MISSING_TABLE = new Set(['42P01', 'PGRST205'])

function describe(error: unknown): string {
  const detail = error as { message?: string; code?: string } | null
  if (!detail) return ''
  return `${detail.message ?? String(error)}${detail.code ? ` (${detail.code})` : ''}`
}

function missing(error: unknown): boolean {
  return MISSING_TABLE.has((error as { code?: string })?.code ?? '')
}

const NOT_INSTALLED =
  '[supabase] public.transcript_comments does not exist — run supabase/migrations/transcript_comments.sql.'

/** Every comment on one transcript, oldest first. */
export async function fetchTranscriptComments(
  projectSlug: string,
  deliverableKey: string,
  clientName: string,
): Promise<CommentLoad> {
  if (!supabase) return { status: 'ok', comments: [] }

  const project = await projectAccess(projectSlug, clientName)
  /*
   * Looked up live rather than read straight off the cached join, exactly
   * like insertChangeRequest/insertApproval in sharedReview.ts: a deliverable
   * a scheduled worker (or staff) adds after this tab already joined the
   * project is not in that cache yet, and a plain cache miss used to mean
   * comments on it silently read back empty.
   */
  const deliverableId = project ? await resolveDeliverableId(project, deliverableKey) : null
  if (!project || !deliverableId) return { status: 'ok', comments: [] }

  const { data, error } = await supabase
    .from('transcript_comments')
    .select('id, display_name, comment, quote, quote_start, quote_seconds, created_at')
    .eq('project_id', project.id)
    .eq('deliverable_id', deliverableId)
    .order('created_at', { ascending: true })

  if (error) {
    if (missing(error)) {
      console.warn(NOT_INSTALLED)
      return { status: 'ok', comments: [] }
    }
    console.warn(`[supabase] transcript comment read failed: ${describe(error)}`)
    return { status: 'error', message: describe(error) }
  }

  const comments = (data ?? []).map((row: Record<string, unknown>) => {
    const created = String(row.created_at ?? '')
    const at = new Date(created).getTime()
    const start = Number(row.quote_start)
    const seconds = Number(row.quote_seconds)
    return {
      id: String(row.id ?? ''),
      authorName: String(row.display_name ?? 'Client'),
      body: String(row.comment ?? ''),
      quote: String(row.quote ?? ''),
      quoteStart: Number.isFinite(start) ? start : -1,
      /* null and an unparseable value both mean "no timing", never 0:00. */
      quoteSeconds: row.quote_seconds != null && Number.isFinite(seconds) ? seconds : null,
      at: Number.isNaN(at) ? Date.now() : at,
    }
  })

  return { status: 'ok', comments }
}

/** Record one comment. Append-only; nothing is ever updated or deleted. */
export async function insertTranscriptComment(input: {
  projectSlug: string
  deliverableKey: string
  displayName: string
  body: string
  quote: string
  quoteStart: number
  /** Seconds into the media, when the transcript has timings. */
  quoteSeconds?: number | null
  revisionId: string
}): Promise<{ ok: boolean; error?: string }> {
  if (!supabase) return { ok: false, error: 'No database connection.' }
  if (!input.body.trim()) return { ok: false, error: 'The comment is empty.' }

  const project = await projectAccess(input.projectSlug, input.displayName)
  /* Same live lookup as fetchTranscriptComments above — see its comment. */
  const deliverableId = project
    ? await resolveDeliverableId(project, input.deliverableKey)
    : null
  if (!project || !deliverableId) {
    return { ok: false, error: 'This transcript could not be found.' }
  }

  const userId = await ensureAnonSession()

  const seconds = Number(input.quoteSeconds)

  const { error } = await supabase.from('transcript_comments').insert({
    project_id: project.id,
    deliverable_id: deliverableId,
    revision_id: input.revisionId || null,
    user_id: userId,
    display_name: input.displayName,
    quote: input.quote || null,
    quote_start: input.quoteStart >= 0 ? input.quoteStart : null,
    quote_seconds:
      input.quoteSeconds != null && Number.isFinite(seconds) && seconds >= 0 ? seconds : null,
    comment: input.body,
  })

  if (error) {
    if (missing(error)) {
      console.warn(NOT_INSTALLED)
      return { ok: false, error: 'Comments are not available yet.' }
    }
    console.warn(`[supabase] transcript comment insert failed: ${describe(error)}`)
    return { ok: false, error: 'Comment could not be saved.' }
  }

  return { ok: true }
}
